import { Fragment, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useContentToken, useProperty, RichTextContent } from '@vibesites/runtime/react';
import { getEligibleNavPages } from './_navShared';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function ThankYouContent() {
  // Mirror the Sitemap page's layout exactly: an editable intro line above the
  // same centered "pages in navigation" link list. The eligibility logic below
  // is shared with the header and the sitemap (see ./_navShared), so the three
  // never drift.
  const virtualTours = useCollectionBinding<VirtualTour>('thankyoucontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map appears in the nav only when the property has the
  // ApartmentSync (Repli360 / RRAC) IDs to drive its widget, or an explicit
  // editor override on the header. Replicate that check so this list doesn't
  // surface a page the nav hides. (Custom fields are read via useProperty; the
  // override token is header-scoped and read-only here.)
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as string | null | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as string | null | undefined;
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  const pages = useMemo(
    () => getEligibleNavPages(hasVirtualTours, hasInteractiveLeasing).map(([, page]) => page),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  return (
    <section className="pt-8 md:pt-10 pb-12 md:pb-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="thankyoucontent.intro"
          label="Intro Text"
          defaultValue="<p>A leasing professional will contact you shortly.</p>"
          as="div"
          className="text-center font-heading text-base md:text-lg font-normal text-[var(--vibe-color-primary)] mb-5 [&_p]:m-0"
        />
        {pages.map((page, index) => (
          <Fragment key={page.path}>
            {index > 0 && (
              <hr className="mx-auto w-12 border-t border-[var(--vibe-color-border)]" />
            )}
            <a
              href={page.path}
              className="block text-center font-heading text-lg md:text-2xl font-normal text-[var(--vibe-color-primary)] py-3 hover:opacity-80 transition-opacity"
            >
              {page.name}
            </a>
          </Fragment>
        ))}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>A leasing professional will contact you shortly.</p>',
    },
  },
  collectionBindings: {
    'thankyoucontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
